import React, { useState, useContext } from "react";
import { IconButton, IIconProps, Popup, Layer, Overlay, mergeStyleSets, FocusTrapZone, DefaultButton, TextField } from "@fluentui/react";
import { FeedBack, FeedBackStatus} from "../../api";
import styles from "./FeedBackButton.module.css";
import { useBoolean } from '@fluentui/react-hooks';
import { FeedBackList } from "./FeedBackList";
import { trackEvent } from "../../api/telemetric";
import { useTranslation } from "../../state/LanguageProvider";

import ThumbsUp from "../../assets/Thumbs_Up_Icon.svg";
import ThumbsDown from "../../assets/Thumbs_Down_Icon.svg";
import ThumbsUpActive from "../../assets/Thumbs_Up_Icon_Active.svg";
import ThumbsDownActive from "../../assets/Thumbs_Down_Icon_Active.svg";

interface Props {
    onFeedBackClicked: (responseStatus: FeedBack, feedback: string) => void;
    question:string
}

const popupStyles = mergeStyleSets({
    root: {
      background: 'rgba(0, 0, 0, 0.2)',
      bottom: '0',
      left: '0',
      position: 'fixed',
      right: '0',
      top: '0',
    },
    content: {
      background: 'white',
      left: '50%',
      minWidth: '300px',
      maxWidth: '600px',
      width: 'calc(100% - 100px)',
      padding: '0 2em 2em',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      fontFamily: "HelveticaNowTELUS",
    },
  });

  
export const FeedBackButton = ({
    onFeedBackClicked,
    question
}: Props) => {

    const [status, updateStatus] = useState<FeedBack>('DEFAULT');
    const [isPopupVisible, { setTrue: showPopup, setFalse: hidePopup }] = useBoolean(false);
    const [feedbackOptions, setFeedbackOptions] = useState<any[]>([]);
    const [feedbackReason, setFeedBackReason] = useState<string>('');
    const { getTranslation } = useTranslation();
    const handleFeedBackClick = (responseStatus: FeedBack, question:string) => {
        updateStatus(responseStatus)
        onFeedBackClicked(responseStatus, "")
    }

    const onFeedbackSelectionChange=(feedBackOptions:any[])=>{
        setFeedbackOptions(feedBackOptions);
    }

    const onReasonChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setFeedBackReason(newValue || "");
    };

    const onFeedbackSubmit = ()=>{
        const selectedReasons = feedbackOptions.filter(option=>option.selected).map(ele=>ele.text);
        const additionalProperties = { "question": question, "Reason": `Selected Reasons: ${selectedReasons.join(',')} User FeedBack: ${feedbackReason}` };
        updateStatus(FeedBackStatus.DISLIKED);
        onFeedBackClicked(FeedBackStatus.DISLIKED, additionalProperties.Reason);
        trackEvent( "DISLIKEFEEDBACK" ,additionalProperties);
        setFeedBackReason("");
        hidePopup();
    }

    const cancel = () => {
        setFeedBackReason("");
        hidePopup();
    }

    return (
        <>
            <div
                title={getTranslation('like')}
                aria-label={getTranslation('like')}
                role="button"
                tabIndex={0}
                onClick={() => handleFeedBackClick(FeedBackStatus.LIKED, question)}
                className= {`${styles.feedBackIconButtonStyle} ${status === FeedBackStatus.LIKED ? styles.activeButtonContainer : ''}`}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? handleFeedBackClick(FeedBackStatus.LIKED, question) : null)}
            >
                <svg className={`${status === FeedBackStatus.LIKED ? styles.activeButton : styles.inactiveButton}`} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.98777 14.5636C1.53388 14.1336 1.24988 13.5543 1.18776 12.9319C0.915667 11.5881 0.939556 10.2012 1.25777 8.86765C1.39201 8.29263 1.73914 7.78999 2.2291 7.46115C2.71907 7.13231 3.3154 7.00175 3.89776 7.09581C4.24306 7.13935 4.59358 7.11306 4.92854 7.01849C5.2635 6.92391 5.57609 6.76298 5.84776 6.54524C6.13714 6.3062 6.37499 6.01081 6.54689 5.67698C6.71879 5.34315 6.82115 4.97784 6.84776 4.60322L6.98776 3.16173C7.02637 2.89074 7.12325 2.63137 7.27173 2.40151C7.42021 2.17165 7.61674 1.97678 7.84776 1.83034C8.14019 1.62179 8.48878 1.50663 8.84776 1.5C8.99974 1.50026 9.15103 1.52045 9.29776 1.56006C9.89737 1.72153 10.4267 2.07725 10.803 2.57167C11.1793 3.06608 11.3814 3.67134 11.3778 4.2929V6.29498H11.7578C12.5854 6.16688 13.4322 6.3146 14.1678 6.71541C14.5845 7.02275 14.8758 7.47085 14.9878 7.97672C15.0425 8.82688 14.8329 9.67315 14.3878 10.3992L14.1878 10.8998C14.1291 11.0562 14.1053 11.2236 14.1178 11.3903C14.1644 11.9383 14.0931 12.49 13.9085 13.0081C13.7239 13.5262 13.4304 13.9986 13.0478 14.3934C12.6815 14.7679 12.2395 15.0599 11.7515 15.2497C11.2635 15.4396 10.7406 15.5231 10.2178 15.4945L3.88776 15.1241C3.20765 15.1725 2.53296 14.9734 1.98777 14.5636ZM8.10958 6.52775C7.81593 7.06752 7.41753 7.57316 6.93776 7.9567C6.57168 8.2368 6.16318 8.45647 5.72776 8.60738V8.71749C5.26799 10.252 5.26799 11.8878 5.72776 13.4224L10.3278 13.6927H10.4278C10.6794 13.6873 10.9272 13.6304 11.156 13.5254C11.3848 13.4203 11.5896 13.2694 11.7578 13.082C11.9592 12.8717 12.1141 12.6213 12.2123 12.347C12.3105 12.0728 12.3499 11.7809 12.3278 11.4904C12.2957 11.0577 12.3607 10.6234 12.5178 10.2191L12.7178 9.71853C12.9265 9.28795 13.0843 8.8344 13.1878 8.36713C13.1698 8.28167 13.1196 8.20641 13.0478 8.15691C12.6075 8.09339 12.162 8.07327 11.7178 8.09685H11.4678C11.0472 8.14431 10.6222 8.06409 10.2478 7.86661C10.0425 7.73487 9.87122 7.55653 9.74777 7.34607C9.56507 6.96664 9.49231 6.54354 9.53777 6.1248V4.28289C9.54606 4.05821 9.47856 3.83727 9.34614 3.65568C9.21371 3.47408 9.02407 3.3424 8.80777 3.28185L8.64777 4.78341C8.5862 5.39493 8.40322 5.98798 8.10958 6.52775ZM3.77776 8.85764H3.59776C3.39474 8.85764 3.04526 9.02598 2.98776 9.26806C2.73285 10.3413 2.70221 11.4558 2.89776 12.5415C2.99253 13.0071 3.03534 13.0716 3.08374 13.1446C3.09152 13.1563 3.09945 13.1683 3.10776 13.1821C3.16776 13.2822 3.35776 13.2822 3.77776 13.2822C3.44258 11.8264 3.44258 10.3134 3.77776 8.85764Z"/>
                </svg>
            </div>

            <div
                title={getTranslation('dislike')}
                aria-label={getTranslation('dislike')}
                role="button"
                tabIndex={0}
                onClick={showPopup}
                className= {`${styles.feedBackIconButtonStyle} ${status === FeedBackStatus.DISLIKED ? styles.activeButtonContainer : ''}`}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? showPopup() : null)}
            >
                <svg className={`${status === FeedBackStatus.DISLIKED ? styles.activeButton : styles.inactiveButton}`} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.8085 4.06813C14.7464 3.44567 14.4624 2.86642 14.0085 2.43644C13.4633 2.02656 12.7886 1.8275 12.1085 1.87586L5.77853 1.50547C5.25571 1.47691 4.73283 1.56037 4.2448 1.75026C3.75677 1.94015 3.31484 2.2321 2.94853 2.60662C2.56593 3.00141 2.27241 3.4738 2.08782 3.99188C1.90323 4.50997 1.83186 5.06167 1.87853 5.60973C1.89104 5.77638 1.86716 5.94376 1.80853 6.10024L1.60853 6.60076C1.16339 7.32685 0.953793 8.17312 1.00853 9.02328C1.1205 9.52915 1.41182 9.97725 1.82853 10.2846C2.56411 10.6854 3.41088 10.8331 4.23853 10.705H4.61853V12.7071C4.6149 13.3287 4.81702 13.9339 5.19332 14.4283C5.56963 14.9227 6.09893 15.2785 6.69853 15.4399C6.84527 15.4795 6.99656 15.4997 7.14853 15.5C7.50752 15.4934 7.85611 15.3782 8.14853 15.1697C8.37956 15.0232 8.57609 14.8284 8.72457 14.5985C8.87305 14.3686 8.96993 14.1093 9.00853 13.8383L9.14853 12.3968C9.17515 12.0222 9.27751 11.6569 9.44941 11.323C9.62131 10.9892 9.85916 10.6938 10.1485 10.4548C10.4202 10.237 10.7328 10.0761 11.0678 9.98151C11.4027 9.88694 11.7532 9.86065 12.0985 9.90419C12.6809 9.99825 13.2772 9.86769 13.7672 9.53885C14.2572 9.21001 14.6043 8.70737 14.7385 8.13235C15.0567 6.79882 15.0806 5.41186 14.8085 4.06813ZM9.05853 9.0433C8.57877 9.42684 8.18037 9.93248 7.88672 10.4722C7.59308 11.012 7.4101 11.6051 7.34853 12.2166L7.18853 13.7182C6.97223 13.6576 6.78259 13.5259 6.65016 13.3443C6.51774 13.1627 6.45023 12.9418 6.45853 12.7171V10.8752C6.50398 10.4565 6.43123 10.0334 6.24853 9.65393C6.12508 9.44347 5.95378 9.26513 5.74853 9.13339C5.37413 8.93591 4.94906 8.85569 4.52853 8.90315H4.27853C3.83431 8.92673 3.38884 8.90661 2.94853 8.84309C2.87665 8.79359 2.82653 8.71833 2.80853 8.63287C2.91202 8.1656 3.06976 7.71205 3.27853 7.28147L3.47853 6.78095C3.63565 6.37663 3.70056 5.94228 3.66853 5.50963C3.64643 5.21912 3.68576 4.92721 3.78398 4.65296C3.8822 4.3787 4.03709 4.12826 4.23853 3.91798C4.40672 3.73055 4.61153 3.57966 4.84029 3.47463C5.06906 3.3696 5.31691 3.31266 5.56853 3.30734H5.66853L10.2685 3.57762C10.7283 5.11215 10.7283 6.74798 10.2685 8.28251V8.39262C9.83312 8.54353 9.42462 8.7632 9.05853 9.0433ZM12.3985 8.14236H12.2185C12.5537 6.68658 12.5537 5.17355 12.2185 3.71777C12.6385 3.71777 12.8285 3.71777 12.8885 3.81787C12.9485 3.91798 12.9885 3.91798 13.0985 4.45854C13.2941 5.54423 13.2634 6.65866 13.0085 7.73194C12.951 7.97402 12.6016 8.14236 12.3985 8.14236Z"/>
                </svg>
            </div>



            {isPopupVisible && (
                <Layer>
                    <Popup
                        role="dialog"
                        aria-modal="true"
                        onDismiss={hidePopup}
                        className={popupStyles.root}
                    >
                        
                        <Overlay onClick={hidePopup} />
                        <FocusTrapZone>
                        <div role="document" className={popupStyles.content}>
                            <h2>{getTranslation('feedback_form')}</h2>
                            <FeedBackList onSelectionChange={onFeedbackSelectionChange}/>
                            <div className={styles.feedBackTextField}>
                                {feedbackReason.length} {getTranslation('char_limit')}
                                <TextField
                                    multiline
                                    resizable={false}
                                    onChange={onReasonChange}
                                    maxLength={250}
                                    style={{fontFamily: "HelveticaNowTELUS", fontFeatureSettings: '"ss08" on, "ss04" on, "ss01" on, "salt" on',}}
                                />
                            </div>
                           
                            <DefaultButton onClick={onFeedbackSubmit} className={styles.feedBackButtonStyle}>{getTranslation('submit')}</DefaultButton> 
                            <DefaultButton onClick={cancel} className={styles.feedBackButtonStyle}>{getTranslation('cancel')}</DefaultButton>
                        </div>
                        </FocusTrapZone>
                    </Popup>
                </Layer>
            )}
        </>
    );
};